import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { BiChevronDown } from "react-icons/bi";
import { Check } from "lucide-react";

export type DropdownOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

interface DropdownProps {
  options: DropdownOption[];
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  placeholder?: string;
  label?: React.ReactNode;
  labelClassName?: string;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  dropdownClassName?: string;
  showClearButton?: boolean;
  onClear?: () => void;
  maxHeight?: string;
}

const PureDropDown: React.FC<DropdownProps> = ({
  options = [],
  value,
  onChange,
  placeholder,
  label,
  labelClassName,
  multiple = false,
  disabled = false,
  className = "",
  buttonClassName = "",
  dropdownClassName = "",
  showClearButton = true,
  onClear,
  maxHeight = "max-h-60",
}) => {
  const { t } = useTranslation();
  const defaultPlaceholder = useMemo(
    () => placeholder || t("common.selectAnOption"),
    [placeholder, t]
  );
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [dropdownPosition, setDropdownPosition] = useState<"bottom" | "top">(
    "bottom"
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const prevValueRef = useRef<string | string[] | undefined>(undefined);

  // Helper function to check if value is empty
  const isEmptyValue = useCallback(
    (val: string | string[] | undefined): boolean => {
      if (val === undefined || val === null) return true;
      if (val === "") return true;
      if (Array.isArray(val)) {
        if (val.length === 0) return true;
        // Check if all values in array are empty
        return val.every((v) => v === "" || v === null || v === undefined);
      }
      if (typeof val === "string" && val.trim() === "") return true;
      return false;
    },
    []
  );

  // Normalize value to array of strings, filtering out empty values
  const normalizeValue = useCallback(
    (val: string | string[] | undefined): string[] => {
      if (isEmptyValue(val)) return [];
      const values = Array.isArray(val) ? val : [val];
      return values.filter(
        (v): v is string =>
          typeof v === "string" && v !== "" && v !== null && v !== undefined
      );
    },
    [isEmptyValue]
  );

  // Initialize selected values from props
  useEffect(() => {
    const normalized = normalizeValue(value);
    const prevNormalized = normalizeValue(prevValueRef.current);

    // Only update if the normalized values actually changed
    const hasChanged =
      normalized.length !== prevNormalized.length ||
      normalized.some((v, i) => v !== prevNormalized[i]);

    if (hasChanged) {
      setSelectedValues(normalized);
      prevValueRef.current = value;
    }
  }, [value, normalizeValue]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const dropdownElement = buttonRef.current?.closest("[data-dropdown]");

      if (dropdownElement && !dropdownElement.contains(target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Use capture phase to handle clicks before dialog handlers
      document.addEventListener("mousedown", handleClickOutside, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [isOpen]);

  // Memoize valid selected values (only those that exist in options)
  const validSelectedValues = useMemo(() => {
    const optionValues = new Set(options.map((opt) => opt.value));
    return selectedValues.filter((v) => optionValues.has(v));
  }, [selectedValues, options]);

  // Sync validSelectedValues back to selectedValues if options changed and invalidated some selections
  const prevOptionsRef = useRef<DropdownOption[]>(options);
  useEffect(() => {
    // Compare options by creating sets of option values for more robust comparison
    const currentOptionValues = new Set(options.map((opt) => opt.value));
    const prevOptionValues = new Set(
      prevOptionsRef.current.map((opt) => opt.value)
    );
    const optionsChanged =
      options.length !== prevOptionsRef.current.length ||
      currentOptionValues.size !== prevOptionValues.size ||
      [...currentOptionValues].some((v) => !prevOptionValues.has(v));

    // Only sync if options changed AND some selected values became invalid
    if (
      optionsChanged &&
      validSelectedValues.length !== selectedValues.length
    ) {
      setSelectedValues(validSelectedValues);
      // Notify parent if values were invalidated
      if (onChange) {
        onChange(multiple ? validSelectedValues : validSelectedValues[0] || "");
      }
    }

    // Always update the ref to track current options
    if (optionsChanged) {
      prevOptionsRef.current = options;
    }
  }, [options, validSelectedValues, selectedValues, multiple, onChange]);

  const handleOptionClick = useCallback(
    (optionValue: string) => {
      if (disabled) return;

      let newValues: string[];

      if (multiple) {
        if (selectedValues.includes(optionValue)) {
          newValues = selectedValues.filter((v) => v !== optionValue);
        } else {
          newValues = [...selectedValues, optionValue];
        }
      } else {
        newValues = [optionValue];
        setIsOpen(false);
      }

      setSelectedValues(newValues);

      if (onChange) {
        onChange(multiple ? newValues : newValues[0] || "");
      }
    },
    [disabled, multiple, selectedValues, onChange]
  );

  const handleClear = useCallback(() => {
    if (disabled) return;

    setSelectedValues([]);
    if (onChange) {
      onChange(multiple ? [] : "");
    }
    if (onClear) {
      onClear();
    }
    if (!multiple) {
      setIsOpen(false);
    }
  }, [disabled, multiple, onChange, onClear]);

  const getDisplayText = useCallback(() => {
    const valuesToDisplay =
      validSelectedValues.length > 0 ? validSelectedValues : selectedValues;

    if (valuesToDisplay.length === 0) return defaultPlaceholder;

    if (multiple) {
      if (valuesToDisplay.length === 1) {
        const option = options.find((o) => o.value === valuesToDisplay[0]);
        return option?.label || valuesToDisplay[0];
      }
      if (valuesToDisplay.length === options.length)
        return t("common.allSelected");
      return `${valuesToDisplay.length} ${t("common.selected")}`;
    } else {
      const option = options.find((o) => o.value === valuesToDisplay[0]);
      return option?.label || valuesToDisplay[0];
    }
  }, [
    validSelectedValues,
    selectedValues,
    multiple,
    options,
    defaultPlaceholder,
    t,
  ]);

  const calculateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return "bottom" as const;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 240; // Approximate dropdown height (max-h-60 = 240px)

    // Check if we're inside a dialog or modal
    const dialogElement = buttonRef.current.closest(
      '[role="dialog"], .fixed.inset-0'
    );
    const isInDialog = !!dialogElement;

    if (isInDialog) {
      // In dialog context, prefer opening downward unless very close to bottom
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      // Only open upward if there's significantly more space above
      if (spaceAbove > spaceBelow + 100) {
        return "top" as const;
      }
      return "bottom" as const;
    }

    // Original logic for non-dialog contexts
    if (buttonRect.bottom + dropdownHeight > viewportHeight) {
      return "top" as const;
    }

    return "bottom" as const;
  }, []);

  const toggleDropdown = useCallback(() => {
    if (disabled) return;

    if (!isOpen) {
      // Calculate position before opening
      const position = calculateDropdownPosition();
      setDropdownPosition(position);
    }

    setIsOpen(!isOpen);
  }, [disabled, isOpen, calculateDropdownPosition]);

  const isOptionSelected = useCallback(
    (optionValue: string) => {
      return validSelectedValues.includes(optionValue);
    },
    [validSelectedValues]
  );

  const hasSelection = useMemo(
    () => validSelectedValues.length > 0,
    [validSelectedValues]
  );

  return (
    <div className={`flex flex-col ${className}`}>
      {label && (
        <label
          className={
            labelClassName ||
            "mb-2 ml-3 text-sm font-bold text-navy-700 dark:text-white"
          }
        >
          {label}
        </label>
      )}

      <div className="relative" data-dropdown>
        <button
          type="button"
          ref={buttonRef}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-disabled={disabled}
          aria-label={label || defaultPlaceholder}
          className={`
            flex h-[45px] w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm 
            focus:outline-none focus:ring-1
            ${
              disabled
                ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500"
                : "border-gray-300 bg-white text-gray-900 hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-400"
            }
            ${buttonClassName}
          `}
          onClick={toggleDropdown}
          disabled={disabled}
        >
          <span
            className={`block truncate ${
              !hasSelection ? "text-gray-500 dark:text-gray-400" : ""
            }`}
            aria-hidden="true"
          >
            {getDisplayText()}
          </span>
          <span
            className="pointer-events-none flex items-center"
            aria-hidden="true"
          >
            <BiChevronDown
              size={16}
              className={`transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </span>
        </button>

        {isOpen && (
          <div
            role="listbox"
            aria-label={label || defaultPlaceholder}
            aria-multiselectable={multiple}
            className={`
              absolute z-[9999] w-full rounded-lg border border-gray-200 bg-white shadow-lg 
              dark:border-gray-600 dark:bg-gray-800
              ${
                dropdownPosition === "top"
                  ? "bottom-full mb-1"
                  : "top-full mt-1"
              }
              ${dropdownClassName}
            `}
            onClick={(e) => e.stopPropagation()}
            style={{
              // Ensure dropdown appears above dialog backdrop
              position: "absolute",
              zIndex: 9999,
            }}
          >
            <div className={`${maxHeight} overflow-y-auto`}>
              {/* Clear button */}
              {showClearButton && hasSelection && (
                <div className="border-b border-gray-200 px-3 py-2 dark:border-gray-600">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClear();
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    aria-label={t("common.clearAll")}
                  >
                    {t("common.clearAll")}
                  </button>
                </div>
              )}

              {options.length > 0 ? (
                options.map((option) => {
                  const isSelected = isOptionSelected(option.value);
                  return (
                    <div
                      key={option.value}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={option.disabled}
                      className={`
                        flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors
                        ${
                          option.disabled
                            ? "cursor-not-allowed text-gray-400 dark:text-gray-500"
                            : "text-gray-900 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
                        }
                        ${
                          isSelected && !option.disabled
                            ? "bg-blue-50 dark:bg-blue-900/20"
                            : ""
                        }
                      `}
                      onClick={() =>
                        !option.disabled && handleOptionClick(option.value)
                      }
                    >
                      {multiple ? (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}} // Handled by onClick
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800"
                          disabled={option.disabled}
                          tabIndex={-1}
                          aria-hidden="true"
                        />
                      ) : (
                        <div
                          className="flex h-4 w-4 items-center justify-center"
                          aria-hidden="true"
                        >
                          {isSelected && (
                            <Check
                              size={14}
                              className="text-blue-600 dark:text-blue-400"
                            />
                          )}
                        </div>
                      )}
                      <span className="flex-1">{option.label}</span>
                    </div>
                  );
                })
              ) : (
                <div
                  role="status"
                  aria-live="polite"
                  className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400"
                >
                  {t("common.noOptionsAvailable")}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PureDropDown;
